import React from 'react'
import styled from '@emotion/styled'
import { PillButton } from '@mobi/component-library/Common/V2'
import { colors, spacing } from '@mobi/component-library/Theme/Common'
import { FollowMeProfile } from '@core/Areas/FollowMe/types'
import { useSharedBetsData } from '@core/Areas/FollowMe/hooks/useSharedBetsData'
import { trackFollowMeProfileTabAndPillChange } from '@core/Areas/FollowMe/helpers/analytics'
import { PendingTips } from './Components/PendingTips'
import { Last10Tips } from './Components/Last10Tips'
import { MyCopiedTips } from './Components/MyCopiedTips'

export const ProfileTips: React.FC<{
  isMyProfile: boolean
  profile: FollowMeProfile
  setHasCopiedBets: (hasBets: boolean) => void
}> = ({ isMyProfile, profile, setHasCopiedBets }) => {
  const [selectedTipsOption, setSelectedTipsOption] = React.useState<TipOptions>('Pending')

  const { isLoading, data, refetch, isRefetching, LoadMoreButton } = useSharedBetsData({
    accountId: profile.id,
    sortOrder: 'sortDesc',
    sortFilter: 'ShareTime',
  })

  const profilePendingBetsCount = (data?.pages.flat() ?? []).length

  const tipOptions: TipOptions[] = isMyProfile
    ? ['Pending', 'Last 10', 'My Copies']
    : ['Pending', 'Last 10']

  return (
    <WrapperStyled>
      <div>
        {tipOptions.map(option => (
          <PillButton
            key={option}
            size='sm'
            onClick={() => {
              setSelectedTipsOption(option)
              trackFollowMeProfileTabAndPillChange('Pill Change', 'Tips', option)
            }}
            color={selectedTipsOption === option ? 'primary' : 'secondary_color'}
          >
            {option}

            {option === 'Pending' && profilePendingBetsCount > 0 ? (
              <span>{profilePendingBetsCount}</span>
            ) : null}
          </PillButton>
        ))}
      </div>

      {selectedTipsOption === 'Pending' && (
        <PendingTips
          data={data}
          refetch={refetch}
          isLoading={isLoading || isRefetching}
          LoadMoreButton={LoadMoreButton}
        />
      )}

      {selectedTipsOption === 'Last 10' && <Last10Tips />}

      {selectedTipsOption === 'My Copies' && <MyCopiedTips setHasCopiedBets={setHasCopiedBets} />}
    </WrapperStyled>
  )
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  '> div:first-of-type': {
    display: 'flex',
    gap: spacing.smx1,
    padding: spacing.smx1,
    background: colors.white,
    borderBottom: `0.1rem solid ${colors.neutral[200]}`,

    '> button > span': {
      borderRadius: spacing.md,
      backgroundColor: colors.lavender[100],
      padding: `0.2rem ${spacing.smx1}`,
      color: colors.lavender[600],
      display: 'flex',
      height: spacing.md,
      justifyContent: 'center',
      alignItems: 'center',
    },
  },
})

// =====
// Types
// =====

type TipOptions = 'Pending' | 'Last 10' | 'My Copies'
